import { useEffect, useState } from 'react';
import { X, DownloadSimple } from '@phosphor-icons/react';
import { getDeferredInstallPrompt, clearDeferredInstallPrompt, isNativeApp } from '../lib/pwa';
import { LEGAL_VERSION } from '../legal/content';

const ACK_KEY = 'fz_install_banner_dismissed';
const DISCLAIMER_ACK_KEY = `fz_legal_visitor_ack_${LEGAL_VERSION}`;

const InstallAppBanner = () => {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(ACK_KEY));
  // Wait for the visitor disclaimer banner to be acknowledged first — both banners
  // anchor to the same fixed bottom position on mobile, so showing them together
  // makes the install CTA overlap/hide behind the disclaimer's "Got it" button.
  const [disclaimerAcked, setDisclaimerAcked] = useState(() => !!localStorage.getItem(DISCLAIMER_ACK_KEY));

  useEffect(() => {
    if (isNativeApp()) return;        // Already inside the Capacitor wrapper — no banner needed.
    const onAvail = () => setAvailable(true);
    const onInstalled = () => setAvailable(false);
    const onDisclaimerAck = () => setDisclaimerAcked(true);
    window.addEventListener('fz-install-available', onAvail);
    window.addEventListener('fz-installed', onInstalled);
    window.addEventListener('fz-disclaimer-ack', onDisclaimerAck);
    return () => {
      window.removeEventListener('fz-install-available', onAvail);
      window.removeEventListener('fz-installed', onInstalled);
      window.removeEventListener('fz-disclaimer-ack', onDisclaimerAck);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(ACK_KEY, new Date().toISOString());
    setDismissed(true);
  };

  const install = async () => {
    const prompt = getDeferredInstallPrompt();
    if (!prompt) return;
    prompt.prompt();
    try { await prompt.userChoice; } catch (_) {}
    clearDeferredInstallPrompt();
    setAvailable(false);
  };

  if (!available || dismissed || !disclaimerAcked || isNativeApp()) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:bottom-4 sm:left-auto sm:right-4 sm:w-[420px] z-40 bg-gradient-to-r from-[#facc15]/15 via-[#facc15]/8 to-transparent backdrop-blur-xl border border-[#facc15]/40 rounded-xl shadow-2xl p-4"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="install-app-banner"
    >
      <div className="flex items-start gap-3">
        <DownloadSimple size={22} weight="fill" className="text-[#facc15] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-xs sm:text-[13px] text-white/90 leading-relaxed">
          <strong className="text-white">Install the Fanzingy app</strong>
          <p className="text-white/65 font-light mt-0.5">Faster, offline-friendly, push notifications for your podium ranks. No app store needed.</p>
        </div>
        <button onClick={install} data-testid="install-app-cta" className="bg-white text-black font-bold text-xs px-3 py-2 rounded-full hover:scale-105 transition-transform whitespace-nowrap">
          Install
        </button>
        <button onClick={dismiss} aria-label="Dismiss" className="text-white/70 hover:text-white" data-testid="install-app-dismiss">
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default InstallAppBanner;
